import { Map, Set } from 'immutable'
import { combineReducers } from 'redux-immutable'
import { handleActions, combineActions } from 'redux-actions'
import timeline from './timeline'
import attributes from './attribute'
import segments from './segment'
import { keyBy } from 'utils/immutable'
import * as actions from './actions'
import * as constants from './constants'

const statusById = handleActions(
  {
    [combineActions(
      actions.fetchEndUserPending,
      actions.fetchEndUserFailed,
      actions.receiveEndUser
    )]: (state, action) => state.set(action.payload.id, action.type),

    [combineActions(
      actions.fetchEndUserListPending,
      actions.fetchEndUserListFailed,
      actions.fetchEndUserListSuccess,
    )]: (state, action) => {
      const { ids } = action.payload
      return state.withMutations(_state => {
        return ids.reduce((result, id) => result.set(id, action.type), _state)
      })
    },
  },
  Map()
)

const unsubscribedCount = handleActions(
  {
    [actions.fetchUnsubscribedCountSuccess]: (state, action) => action.payload,
  },
  0
)

const unsubscribed = handleActions(
  {
    [actions.checkUnsubscribedSuccess]: (state, action) => state.set('value', action.payload),
    [combineActions(
      actions.unsubscribe,
      actions.unsubscribeSuccess,
    )]: (state) => state.set('value', true),
    [combineActions(
      actions.unsubscribe,
      actions.unsubscribeById,
      actions.unsubscribePending,
      actions.unsubscribeFailed,
      actions.unsubscribeSuccess,
    )]: (state, action) => state.set('status', action.type),
    [actions.unsubscribeFailed]: (state, action) => {
      const { response, status } = action.payload
      if (status === 400) {
        return state.set('errorMessage', response.message)
      } else {
        return state.set('errorMessage', 'Something went wrong, please try again.')
      }
    },
    [combineActions(
      actions.requestUnsubDeleteData,
      actions.requestUnsubDeleteDataPending,
      actions.requestUnsubDeleteDataSuccess,
      actions.requestUnsubDeleteDataFailed,
    )]: (state, action) => state.set('requestUnsubDeleteDataStatus', action.type)
  },
  Map({ value: false })
)

const resubscribed = handleActions(
  {
    [combineActions(
      actions.resubscribe,
      actions.resubscribeSuccess,
    )]: (state) => state.set('value', true),
    [combineActions(
      actions.resubscribe,
      actions.resubscribePending,
      actions.resubscribeFailed,
      actions.resubscribeSuccess,
    )]: (state, action) => state.set('status', action.type),
    [actions.resubscribeFailed]: (state, action) => {
      const { response, status } = action.payload
      if (status === 400) {
        return state.set('errorMessage', response.message)
      } else {
        return state.set('errorMessage', 'Something went wrong, please try again.')
      }
    }
  },
  Map({ value: false })
)

const byId = handleActions(
  {
    [actions.receiveEndUser]: (state, action) => {
      const existingEndUser = state[action.payload.id]
      const isNewerVersion = existingEndUser &&
        action.payload.getIn(['attributes', '_end_user_version']) >= existingEndUser.getIn(['attributes', '_end_user_version'])
      if (!existingEndUser || isNewerVersion) {
        return state.set(action.payload.id, action.payload)
      }
      return state
    },
    [actions.updateCQL]: (state, action) => {
      const { endUserId, score } = action.payload
      return state.setIn([endUserId, 'attributes', '_cql_score'], score)
    },
    [actions.fetchEndUserListSuccess]: (state, action) => {
      const { data } = action.payload
      return state.merge(keyBy(data))
    },
  },
  Map()
)

const propDefs = handleActions(
  {
    [actions.createPropertyDefinitionPending]: (state, action) =>
      state.setIn(['newlyCreated', action.payload], 'PENDING'),
    [actions.createPropertyDefinitionSuccess]: (state, action) =>
      state.setIn(['newlyCreated', action.payload], 'CREATED'),
    [combineActions(actions.createPropertyDefinitionCancelled, actions.createPropertyDefinitionFailed)]: (
      state,
      action
    ) => state.setIn(['newlyCreated', action.payload.name], 'FAILED'),
  },
  Map({ newlyCreated: Map() })
)

const following = handleActions(
  {
    [combineActions(actions.fetchFollowingSuccess, actions.updateFollowingSuccess)]: (state, action) =>
      state.setIn(['data', action.payload.id], action.payload.following),
    [combineActions(
      actions.fetchFollowing,
      actions.fetchFollowingPending,
      actions.fetchFollowingFailed,
      actions.fetchFollowingSuccess
    )]: (state, action) => state.setIn(['fetchFollowingState', action.payload.id], action.type),
    [combineActions(
      actions.updateFollowing,
      actions.updateFollowingPending,
      actions.updateFollowingFailed,
      actions.updateFollowingSuccess
    )]: (state, action) => state.setIn(['updateFollowingState', action.payload.id], action.type),
  },
  Map({ data: Map(), fetchFollowingState: Map(), updateFollowingState: Map() })
)

const retrieval = handleActions(
  {
    [combineActions(actions.requestContactData, actions.requestContactDataPending)]: state =>
      state.set('status', constants.REQUEST_CONTACT_DATA_PENDING),
    [actions.requestContactDataSuccess]: state => state.set('status', constants.REQUEST_CONTACT_DATA_SUCCESS),
    [actions.requestContactDataFailed]: state => state.set('status', constants.REQUEST_CONTACT_DATA_FAILED),
  },
  Map({
    status: null,
  })
)
const deletion = handleActions(
  {
    [combineActions(actions.requestDeleteContactData, actions.requestDeleteContactDataPending)]: state =>
      state.set('status', constants.REQUEST_DELETE_CONTACT_DATA_PENDING),
    [actions.requestDeleteContactDataSuccess]: state =>
      state.set('status', constants.REQUEST_DELETE_CONTACT_DATA_SUCCESS),
    [actions.requestDeleteContactDataFailed]: state =>
      state.set('status', constants.REQUEST_DELETE_CONTACT_DATA_FAILED),
  },
  Map({
    status: null,
  })
)

const activeEndUserChannels = handleActions({
  [actions.joinEndUserChannel]: (state, action) => state.add(action.payload),
  [actions.closeEndUserChannel]: (state, action) => state.delete(action.payload)
}, Set())

export default combineReducers({
  byId,
  statusById,
  following,
  attributes,
  segments,
  timeline,
  unsubscribed,
  unsubscribedCount,
  resubscribed,
  propDefs,
  retrieval,
  deletion,
  activeEndUserChannels,
})



// WEBPACK FOOTER //
// ./src/scripts/app/modules/endUser/reducer.js